import { ReducerChain, withScope } from "@gamedev-web/gamedev-sdk-core";
import { PartnerCenterEnrollmentStatus } from "@gamedev-web/one-gaming-crm";

import {
  XboxOnboardApplicationStatus,
  XboxOnboardResponse,
} from "../../server/crm/api/models/xboxOnboardModels";
import { XboxOnboardActions, xboxOnboardActions } from "../actions/xboxOnboardActions";
import { XboxOnboardScope } from "../scopes";

export type XboxOnboardState = {
  isLoading: boolean;
  isSubmitting: boolean;
  data: XboxOnboardResponse | null;
  error: string | null;
};

export const initialXboxOnboardState: XboxOnboardState = {
  isLoading: false,
  isSubmitting: false,
  data: null,
  error: null,
};

const submittedStatuses = new Set<XboxOnboardApplicationStatus>([
  XboxOnboardApplicationStatus.Submitted,
  XboxOnboardApplicationStatus.NdaSent,
  XboxOnboardApplicationStatus.NdaSigned,
]);

export const preserveSubmittedXboxOnboardData = (
  current: XboxOnboardResponse | null,
  incoming: XboxOnboardResponse
): XboxOnboardResponse => {
  const incomingIsStale =
    incoming.status === XboxOnboardApplicationStatus.Initial ||
    incoming.status === XboxOnboardApplicationStatus.FormReady;
  return current && submittedStatuses.has(current.status) && incomingIsStale ? current : incoming;
};

export const xboxOnboardReducer = withScope(
  [XboxOnboardScope],
  new ReducerChain<XboxOnboardState>(initialXboxOnboardState)
    .on(XboxOnboardActions.UPDATE_DEVELOPMENT_HARDWARE_ACCESS, (state, action) => {
      const { hasAccess } = action as ReturnType<
        typeof xboxOnboardActions.updateDevelopmentHardwareAccess
      >;
      return {
        ...state,
        data: state.data
          ? {
              ...state.data,
              hasDevelopmentHardwareAccess: hasAccess,
            }
          : state.data,
      };
    })
    .on(XboxOnboardActions.FETCH_DATA_START, state => ({
      ...state,
      isLoading: true,
      error: null,
    }))
    .on(XboxOnboardActions.FETCH_DATA_COMPLETED, (state, action) => {
      const data = (action as unknown as { data: XboxOnboardResponse }).data;
      return {
        ...state,
        isLoading: false,
        data: preserveSubmittedXboxOnboardData(state.data, data),
        error: null,
      };
    })
    .on(XboxOnboardActions.FETCH_DATA_ERROR, (state, action) => ({
      ...state,
      isLoading: false,
      error: (action as unknown as { error: Error }).error.message,
    }))
    .on(XboxOnboardActions.SUBMIT_FORM_START, state => ({
      ...state,
      isSubmitting: true,
      error: null,
    }))
    .on(XboxOnboardActions.SUBMIT_FORM_COMPLETED, (state, action) => ({
      ...state,
      isSubmitting: false,
      data: (action as unknown as { data: XboxOnboardResponse }).data,
      error: null,
    }))
    .on(XboxOnboardActions.SUBMIT_FORM_ERROR, (state, action) => ({
      ...state,
      isSubmitting: false,
      error: (action as unknown as { error: Error }).error.message,
    }))
    .on(XboxOnboardActions.POLL_NDA_COMPLETED, (state, action) => {
      const data = (action as unknown as { data: XboxOnboardResponse }).data;
      return {
        ...state,
        data: preserveSubmittedXboxOnboardData(state.data, data),
      };
    })
    .on(XboxOnboardActions.POLL_ENROLLMENT_COMPLETED, (state, action) => ({
      ...state,
      data: (action as unknown as { data: XboxOnboardResponse }).data,
    }))
    .on(XboxOnboardActions.START_ENROLLMENT_START, state => ({
      ...state,
      error: null,
      data: state.data
        ? {
            ...state.data,
            partnerCenterEnrollmentStatus: PartnerCenterEnrollmentStatus.EarlyInProgress,
          }
        : state.data,
    }))
    .on(XboxOnboardActions.START_ENROLLMENT_COMPLETED, (state, action) => ({
      ...state,
      data: (action as unknown as { data: XboxOnboardResponse }).data,
    }))
    .on(XboxOnboardActions.START_ENROLLMENT_ERROR, (state, action) => ({
      ...state,
      error: (action as unknown as { error: Error }).error.message,
    }))
    .on(XboxOnboardActions.RETRY_ENROLLMENT_START, state => ({
      ...state,
      error: null,
      data: state.data
        ? {
            ...state.data,
            partnerCenterEnrollmentStatus: PartnerCenterEnrollmentStatus.EarlyInProgress,
          }
        : state.data,
    }))
    .on(XboxOnboardActions.RETRY_ENROLLMENT_COMPLETED, (state, action) => ({
      ...state,
      data: (action as unknown as { data: XboxOnboardResponse }).data,
    }))
    .on(XboxOnboardActions.RETRY_ENROLLMENT_ERROR, (state, action) => ({
      ...state,
      error: (action as unknown as { error: Error }).error.message,
    }))
    .on(XboxOnboardActions.RESEND_NDA_START, state => ({
      ...state,
      isSubmitting: true,
      error: null,
    }))
    .on(XboxOnboardActions.RESEND_NDA_COMPLETED, (state, action) => {
      const responseData = (action as unknown as { data: XboxOnboardResponse }).data;
      return {
        ...state,
        isSubmitting: false,
        data: responseData?.status ? responseData : state.data,
      };
    })
    .on(XboxOnboardActions.RESEND_NDA_ERROR, (state, action) => ({
      ...state,
      isSubmitting: false,
      error: (action as unknown as { error: Error }).error.message,
    }))
    .build()
);
